import { APP_CONFIG, PRIMARY_BASE, FALLBACK_BASE } from './config';

// ─────────────────────────────────────────────
// Upload CV to server
// Returns the public URL of the uploaded file
// ─────────────────────────────────────────────

// meta: { branch_name, company_name } — passed so PHP can name folder/file correctly
export async function uploadCV(file, meta = {}) {
  if (!file) throw new Error('UPLOAD_ERROR: No file provided');

  // ── Client side validation ────────────────
  const allowedTypes = ['application/pdf', 'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  const allowedExts  = ['pdf', 'doc', 'docx'];
  const ext          = file.name.split('.').pop().toLowerCase();
  const maxSize      = 2 * 1024 * 1024; // 2MB

  if (!allowedExts.includes(ext)) {
    throw new Error('UPLOAD_ERROR: Only PDF, DOC, DOCX files are allowed');
  }

  if (!allowedTypes.includes(file.type)) {
    throw new Error('UPLOAD_ERROR: Invalid file type');
  }

  if (file.size > maxSize) {
    throw new Error('UPLOAD_ERROR: File size must be under 2MB');
  }

  // ── Build FormData ────────────────────────
  const formData = new FormData();
  formData.append('cv', file); // must match PHP $_FILES['cv']
  if (meta.branch_name)  formData.append('branch_name',  meta.branch_name);
  if (meta.company_name) formData.append('company_name', meta.company_name);
  formData.append('upload_date', new Date().toISOString());

  // ── Upload (tries primary, falls back on network error OR 5xx) ───
  const uploadUrls = [`${PRIMARY_BASE}/upload-cv.php`, `${FALLBACK_BASE}/upload-cv.php`];
  let res, data, lastErr;

  for (const url of uploadUrls) {
    try {
      const r = await fetch(url, { method: 'POST', body: formData });
      let d;
      try { d = await r.json(); } catch { d = {}; }

      if (!r.ok && r.status >= 500) {
        lastErr = new Error(`UPLOAD_FAILED: ${d?.error || r.status}`);
        continue; // server error on this URL — try fallback
      }

      res  = r;
      data = d;
      break;
    } catch (err) {
      lastErr = err; // network failure — try fallback
    }
  }

  if (!res) throw lastErr || new Error('UPLOAD_FAILED: All upload servers unavailable');

  if (!res.ok || !data?.url) {
    throw new Error(`UPLOAD_FAILED: ${data?.error || res.status}`);
  }

  return {
    url:           data.url,           // full public URL
    file_name:     data.file_name,     // saved filename on server
    original_name: data.original_name, // original filename
    size_kb:       data.size_kb,
  };
}